// Static content for the Logarithmic Drive website.
// Keep content in one file so copy changes don't require hunting through components.

export const BRAND = {
  company: "Logarithmic Technologies",
  short: "LogDrive",
  product: "Logarithmic Drive",
  long: "Logarithmic Technologies",
  tagline: "The first practical reducer technology in 50 years.",
  brandTagline: "Driving What's Next",
  contactEmail: "partnerships@logarithmictech.com",
  phone: "(949) 306-3585",
  address: "32362 Ascension Road, Dana Point, CA 92629",
  logo: "/brand/logo.png",
  logoIcon: "/brand/logo-icon.png",
};

export const HERO_STATS = [
  { k: "Ratios", v: "1:1 – 225:1" },
  { k: "Efficiency", v: "75 – 95%" },
  { k: "Backlash", v: "~0" },
  { k: "kW / kg", v: "3× Current Tech" },
  { k: "Nm / kg", v: "4× Current Tech" },
];

export const APPLICATIONS = [
  {
    slug: "defense",
    kicker: "01 / Defense",
    title: "Rotorcraft, UAS & Actuation",
    blurb:
      "A two-stage LogDrive with counter-rotating shafts replaces conventional planetary arrangements — cutting weight, part count, and failure modes in platforms like the V-22 Osprey.",
    bullets: [
      "V-22 Osprey alternative gearbox architecture",
      "Compact turret & actuator drives for UAS / UGV",
      "Higher MTBF via fewer meshing contact points",
    ],
    image:
      "https://images.pexels.com/photos/14577442/pexels-photo-14577442.jpeg",
  },
  {
    slug: "aerospace",
    kicker: "02 / Aerospace & eVTOL",
    title: "Electric & Distributed Propulsion",
    blurb:
      "Hollow-shaft, coaxial form factor makes LogDrive a natural fit for eVTOL rotor drives, distributed propulsion and lightweight actuation — where every gram of torque density matters.",
    bullets: [
      "High torque density in a compact coaxial envelope",
      "Hollow shaft for wiring, cooling, and sensing pass-through",
      "Low vibration profile for stability-critical platforms",
    ],
    image:
      "https://images.unsplash.com/photo-1731579884271-0abb3c5cb99c",
  },
  {
    slug: "marine",
    kicker: "03 / Marine",
    title: "Contra-Rotating Propellers",
    blurb:
      "Configured as MINV or SP-MINV, LogDrive simplifies contra-rotating propeller systems. Field data on contra-rotating configurations shows up to 16% fuel-efficiency gain with reduced torque and vibration.",
    bullets: [
      "Motion Inverter (MINV) — single input, two contra-rotary outputs",
      "Speed Reducer + Motion Inverter (SP-MINV) in one compact unit",
      "Drop-in for classic ICE and electric outboards",
    ],
    image:
      "https://images.unsplash.com/photo-1738918917045-ac8cf1289a48",
  },
  {
    slug: "energy",
    kicker: "04 / Energy",
    title: "Wind, Hydro, Power Gen",
    blurb:
      "LogDrive’s load-sharing bearing-ball architecture is engineered for the long-duty, high-cycle environments of energy generation — pitch/yaw drives, hydro turbines, and flywheel systems. The counter-rotating output drives the industry’s highest power-to-weight generators.",
    bullets: [
      "High cycle life from distributed ball contact",
      "Hollow shaft simplifies roll-ring & sensor integration",
      "Sealed, service-friendly footprint",
      "Counter-rotating output → highest power-to-weight generators in class",
    ],
    image:
      "https://images.pexels.com/photos/4065437/pexels-photo-4065437.jpeg",
  },
  {
    slug: "robotics",
    kicker: "05 / Robotics & Actuators",
    title: "Actuators & Drives",
    blurb:
      "LogDrive’s counter-rotating capability is the basis for a new generation of actuators and drives. By pairing two coaxial, opposed outputs in a single compact unit, we build the highest power-to-weight and torque-to-weight actuators and generators in the industry — ready for humanoid, industrial, and defense-grade robotic platforms.",
    bullets: [
      "Highest power-to-weight in industry — counter-rotating topology",
      "Highest torque-to-weight actuators for humanoid & exoskeleton joints",
      "Hollow shaft for wiring, cooling, and sensor pass-through",
      "Direct-drive or geared configurations — SP / MINV / SP-MINV",
    ],
    image:
      "https://images.unsplash.com/photo-1581092921461-eab62e97a780",
  },
];

export const TECH_PILLARS = [
  {
    id: "input",
    label: "INPUT",
    body:
      "A low-angle wedge element, continuously engaged by the input shaft. The surface geometry distributes radial force across every bearing ball simultaneously — no single tooth meshing, no single point of failure.",
  },
  {
    id: "modulator",
    label: "MODULATOR",
    body:
      "The Modulator carries a bank of bearing balls constrained to travel radially. It converts the linear wedge principle into continuous rotary motion while sharing load across the entire bearing array.",
  },
  {
    id: "output",
    label: "OUTPUT",
    body:
      "A high-angle wedge element. Every ball pushes on its surfaces at once, producing amplified torque and a reduced angular velocity — the reduction ratio.",
  },
];

export const TEAM = [
  {
    name: "Sorin Cora",
    role: "CTO & Founder",
    bio:
      "Inventor of the Logarithmic Drive. 30+ years of engineering practice. MSc Aeronautics, University of Bucharest. MSc Applied Science, University of Montreal. Mathematician first, mechanical designer second.",
    image: "/team/sorin.png",
  },
  {
    name: "Jorg Lorscheider",
    role: "CEO",
    bio:
      "Serial entrepreneur, active investor across deep-tech and medical devices. Long-time collaborator with the founder. Leads commercial strategy, partnerships and capital formation.",
    image: "/team/jorg.png",
  },
];

export const ADVISORS = [
  {
    name: "Greg Langley",
    focus: "Commercialization & Go-to-Market",
    bio: "Entrepreneur and product-development leader with 30+ years building novel medical-device and aerospace products — formerly a leader at Syncroness, an INC 5000 honoree for seven consecutive years — specializing in commercialization, Design Controls (CFR 820), and ISO 9001 / ISO 13485 manufacturing and quality systems.",
  },
  {
    name: "Former rotorcraft transmission chief — US prime",
    focus: "advisor - future",
  },
  {
    name: "Power-electronics architect — eVTOL OEM",
    focus: "advisor - future",
  },
  {
    name: "Marine propulsion program manager — outboard tier 1",
    focus: "advisor - future",
  },
  {
    name: "Energy storage flywheel engineering lead",
    focus: "advisor - future",
  },
];

export const TARGET_PARTNERS = [
  { name: "Anduril Industries", focus: "Autonomous Defense" },
  { name: "Tesla", focus: "Electric Powertrain / Actuation" },
  { name: "Verdagro Aero", focus: "Advanced Air Mobility" },
  { name: "Lockheed Martin", focus: "Rotorcraft & UAS" },
  { name: "General Atomics", focus: "UAS Propulsion" },
  { name: "Boeing", focus: "Rotorcraft Gearboxes" },
  { name: "RTX / Pratt & Whitney", focus: "Propulsion Integration" },
  { name: "Northrop Grumman", focus: "Defense Systems" },
];

export const NEWS = [
  {
    date: "2024.09.03",
    category: "Research",
    title: "LogDrive configurations for contra-rotating marine propellers",
    summary:
      "A full teardown of MINV / SP-MINV configurations, including power-head vs. lower-unit layouts and belt-driven alternatives.",
  },
  {
    date: "2024.06.27",
    category: "Technology",
    title: "How the Logarithmic Drive works — principles and geometry",
    summary:
      "A walkthrough of the input / modulator / output architecture, bearing-ball load sharing, and the wedge principle behind high-efficiency reduction.",
  },
  {
    date: "2024.06.21",
    category: "Engineering",
    title: "Contra-rotating propellers — simple and efficient with LogDrive",
    summary:
      "Why the historical downsides of contra-rotating systems (complexity, maintenance, cost) evaporate with a single LogDrive unit.",
  },
];

export const CAREERS = [
  {
    title: "Defense Programs Lead",
    location: "Washington, D.C. / Remote",
    type: "Full-time",
    summary:
      "Own defense customer relationships, BAAs and SBIR programs. Prior USG program experience and active clearance preferred.",
  },
  {
    title: "Test Engineer — Propulsion Rigs",
    location: "Dana Point, CA",
    type: "Full-time",
    summary:
      "Design and operate dynamometer, endurance and vibration rigs. Own instrumentation, DAQ, and reporting for partner evaluations.",
  },
  {
    title: "Manufacturing Engineer (NPI)",
    location: "Dana Point, CA",
    type: "Full-time",
    summary:
      "Bring LogDrive from prototype to low-rate production. Precision machining, inspection, supply-chain qualification.",
  },
];

export const NAV = [
  { to: "/", label: "Overview" },
  { to: "/technology", label: "Technology" },
  { to: "/applications", label: "Applications" },
  { to: "/team", label: "Team" },
  { to: "/partners", label: "Partners" },
  { to: "/news", label: "News" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];
